import {
  GET_APPLICATION_FORMS,
  GET_USER_PROFILE_DETAILS_URL,
  SUBMIT_APPLICATION_FORMS_ANSWERS,
  get_headers,
} from '@/utils/constants/constants';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';

function normalize_answer_value(raw_answer) {
  if (raw_answer === null || raw_answer === undefined || raw_answer === '') {
    return 'Not provided';
  }
  if (Array.isArray(raw_answer)) {
    if (!raw_answer.length) return 'Not provided';
    return raw_answer.map(item => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
  }
  if (typeof raw_answer === 'object') {
    if (raw_answer.answer !== undefined) return normalize_answer_value(raw_answer.answer);
    if (raw_answer.answer_text !== undefined) return normalize_answer_value(raw_answer.answer_text);
    const keys = Object.keys(raw_answer);
    if (!keys.length) return 'Not provided';
    return JSON.stringify(raw_answer);
  }
  return String(raw_answer);
}

function parse_multi_select_answer(raw_answer) {
  if (!raw_answer) return [];
  if (Array.isArray(raw_answer)) {
    return raw_answer.map(item =>
      typeof item === 'object' ? (item.choice || item.answer || String(item)) : String(item)
    );
  }
  if (typeof raw_answer === 'object') {
    if (raw_answer.answer !== undefined) return parse_multi_select_answer(raw_answer.answer);
    if (raw_answer.answer_text !== undefined) return parse_multi_select_answer(raw_answer.answer_text);
    return [];
  }
  if (typeof raw_answer === 'string' && raw_answer.trim()) {
    return raw_answer.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
}

export default {
  name: 'ProfileForm',
  mixins: [alert_mixin],
  data: () => ({
    headers: {},
    is_editing: false,
    show_page_help: false,
    is_loading: false,
    is_saving: false,
    save_message: 'Unsaved changes',
    profile_title: 'Job Details',
    profile_form_id: null,
    has_profile_form: true,
    profile_questions: [],
    hidden_synced_questions: [],
    work_experience_blocks: [],
    identity_defaults: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
    },
  }),
  mounted() {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) {
      this.$router.replace({ name: 'UserLogin' });
      return;
    }
    this.headers = get_headers();
    this.fetch_user_details().then(() => {
      this.fetch_profile_info();
    });
  },
  methods: {
    format_date(date_value) {
      if (!date_value) return 'Not provided';
      const parsed = new Date(date_value);
      if (Number.isNaN(parsed.getTime())) return String(date_value);
      return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    async fetch_user_details() {
      const response = await axios.get(GET_USER_PROFILE_DETAILS_URL, { headers: this.headers });
      const user_data = response?.data?.data || {};
      this.identity_defaults = {
        first_name: user_data.first_name || '',
        last_name: user_data.last_name || '',
        email: user_data.email || '',
        phone_number: user_data.phone_number || '',
      };
    },

    get_identity_key(question_label) {
      const label = String(question_label || '').toLowerCase();
      if (/(first\s*name|name\s*of\s*applicant|given\s*name)/.test(label)) return 'first_name';
      if (/(last\s*name|surname|family\s*name)/.test(label)) return 'last_name';
      if (/(email|e-mail)/.test(label)) return 'email';
      if (/(phone|mobile|contact\s*number|telephone|cell\s*number)/.test(label)) return 'phone_number';
      return null;
    },

    get_identity_default_value(question_label) {
      const label = String(question_label || '').toLowerCase();
      if (/(first\s*name|name\s*of\s*applicant|given\s*name)/.test(label)) return this.identity_defaults.first_name || '';
      if (/(last\s*name|surname|family\s*name)/.test(label)) return this.identity_defaults.last_name || '';
      if (/(email|e-mail)/.test(label)) return this.identity_defaults.email || '';
      if (/(phone|mobile|contact\s*number|telephone|cell\s*number)/.test(label)) return this.identity_defaults.phone_number || '';
      return '';
    },

    async fetch_profile_info() {
      this.is_loading = true;
      this.has_profile_form = true;

      try {
        const response = await axios.get(GET_APPLICATION_FORMS, {
          headers: this.headers,
          params: { profile: 1 },
        });

        const response_body = response.data;
        const forms = Array.isArray(response_body)
          ? response_body
          : (Array.isArray(response_body?.data)
            ? response_body.data
            : (response_body?.data ? [response_body.data] : []));

        if (!forms.length) {
          this.has_profile_form = false;
          this.profile_questions = [];
          this.hidden_synced_questions = [];
          return;
        }

        const payload = forms[0] || {};
        this.profile_form_id = payload.application_form_id || payload.id || null;
        const raw_sections = payload?.form?.sections || [];

        this.profile_title = payload.form_name || 'Profile Information';
        const all_questions = raw_sections.flatMap((section, index) => {
          const section_name = section.section_name || section.name || `Section ${index + 1}`;
          return (section.questions || []).map((question, qIndex) => {
            const label = question.question_text || question.question_name || `Question ${qIndex + 1}`;
            const identity_key = this.get_identity_key(label);
            const normalized = normalize_answer_value(question.answer);
            const fallback = this.get_identity_default_value(label);
            const q_type = String(question?.question_type?.question_type || 'short_text').toLowerCase();

            let editable_val;
            if (identity_key) {
              editable_val = this.identity_defaults[identity_key] || '';
            } else if (q_type === 'multi_select') {
              editable_val = parse_multi_select_answer(question.answer);
            } else {
              editable_val = normalized === 'Not provided' ? (fallback || '') : normalized;
            }

            return {
              id: question.section_question_id || question.id || `${index + 1}-${qIndex + 1}`,
              label,
              section_name,
              question_type: q_type,
              options: (question.multi_select_choices || []).map(item => ({ value: item.choice, text: item.choice })),
              identity_key,
              editable_value: editable_val,
            };
          });
        });

        this.hidden_synced_questions = [];

        const we_regex = /^\[WE\s+(\d+)\]\s*(.*)/i;
        const we_map = {};
        const regular_questions = [];
        const identity_shown = new Set();

        all_questions.forEach(q => {
          if (q.identity_key) {
            if (!identity_shown.has(q.identity_key)) {
              identity_shown.add(q.identity_key);
              regular_questions.push(q);
            }
            return;
          }
          const match = q.label.match(we_regex);
          if (match) {
            const block_num = parseInt(match[1], 10);
            const field_label = match[2];
            const lower = field_label.toLowerCase();
            let field_type = 'text';
            if (lower.includes('month')) field_type = 'we_month';
            else if (lower.includes('year')) field_type = 'we_year';
            else if (lower.includes('reason')) field_type = 'textarea';
            if (!we_map[block_num]) we_map[block_num] = [];
            we_map[block_num].push({
              id: q.id,
              label: field_label,
              field_type,
              editable_value: q.editable_value === 'Not provided' ? '' : q.editable_value,
            });
          } else {
            regular_questions.push(q);
          }
        });

        const block_nums = Object.keys(we_map).map(Number).sort();
        const all_we_blocks = block_nums.map((num, idx) => ({
          index: idx,
          label: `Experience ${idx + 1}`,
          fields: we_map[num],
        }));
        this._all_we_blocks = all_we_blocks;
        if (all_we_blocks.length > 0) {
          const with_data = all_we_blocks.filter(b => b.fields.some(f => f.editable_value && f.editable_value.trim()));
          this.work_experience_blocks = with_data.length > 0 ? with_data : [all_we_blocks[0]];
        } else {
          this.work_experience_blocks = [];
        }

        this.profile_questions = regular_questions;
      } catch (error) {
        this.show_error(error?.response?.data?.message || 'Could not load profile form');
        this.has_profile_form = false;
        this.profile_questions = [];
        this.hidden_synced_questions = [];
      } finally {
        this.is_loading = false;
      }
    },

    sync_identity_back_to_questions() {
      // This method ensures that any identity-linked questions are updated with the current identity defaults
      this.profile_questions = this.profile_questions.map(question => {
        if (!question.identity_key) return question;
        return {
          ...question,
          editable_value: this.identity_defaults[question.identity_key] || '',
        };
      });
    },

    add_we_block() {
      const all = this._all_we_blocks || [];
      if (this.work_experience_blocks.length >= all.length || this.work_experience_blocks.length >= 3) return;
      this.work_experience_blocks = [...this.work_experience_blocks, all[this.work_experience_blocks.length]];
    },

    remove_we_block(bidx) {
      if (this.work_experience_blocks.length <= 1) return;
      this.work_experience_blocks = this.work_experience_blocks.filter((_, idx) => idx !== bidx);
    },

    async save_profile_answers() {
      if (!this.profile_form_id) return;

      const visible_answers = this.profile_questions.map(question => ({
        section_question_id: question.id,
        answer_text: Array.isArray(question.editable_value)
          ? question.editable_value.join(', ')
          : (question.editable_value || ''),
      }));

      const synced_identity_answers = this.hidden_synced_questions.map(question => ({
        section_question_id: question.id,
        answer_text: this.identity_defaults[question.identity_key] || '',
      }));

      const we_answers = this.work_experience_blocks.flatMap(block =>
        block.fields.map(f => ({
          section_question_id: f.id,
          answer_text: (f.editable_value || '').toString().trim(),
        }))
      );

      const answers = [...visible_answers, ...synced_identity_answers, ...we_answers];

      const form_data = new FormData();
      form_data.append('application_form_id', String(this.profile_form_id));
      form_data.append('current_section_index', '0');
      form_data.append('answers', JSON.stringify(answers));

      await axios.post(SUBMIT_APPLICATION_FORMS_ANSWERS, form_data, {
        headers: {
          ...this.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
    },

    async update_profile() {
      this.is_saving = true;
      this.save_message = 'Saving...';
      try {
        this.sync_identity_back_to_questions();
        await this.save_profile_answers();

        this.save_message = 'Form updated';
        this.show_success('Profile form updated successfully.');
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Could not update form.';
        this.save_message = 'Unsaved changes';
        this.show_error(msg);
      } finally {
        this.is_saving = false;
        if (this.save_message === 'Form updated') {
          setTimeout(() => { this.save_message = 'Unsaved changes'; }, 2500);
        }
      }
    },
  },
};